/**
 * Componente Sidebar - Panel lateral con carpetas y estadísticas
 * @module components/panels/Sidebar
 */

import { useState, useMemo } from 'react'
import { useNodeStore, useFolderStore, useUIStore, useFiberStore } from '@/stores'
import { NODE_ICONS, NODE_COLORS } from '@/constants'
import {
  filterNodesByFolderView,
  UNFILED_FOLDER_SENTINEL,
  isUnfiledFolderView,
} from '@/constants/folderFilter'
import { cableOrigenId, cableDestinoId } from '@/utils/cableEndpoints'
import { cableLabelShort } from '@/utils/cableDisplayLabel'
import { FolderTree } from './FolderTree'
import { Plus, X, FolderPlus, Check, Cable, ChevronDown, ChevronRight, Trash2, Layers, FileUp, Users } from 'lucide-react'
import type { NodeType, AnyNode } from '@/types/nodes'
import { canChangeNodeType } from '@/utils/canChangeNodeType'

const BULK_TYPES: NodeType[] = ['POP', 'OLT', 'NAP', 'CIERRE', 'CLIENTE', 'POSTE']

export function Sidebar() {
  const sidebarOpen = useUIStore((state) => state.sidebarOpen)
  const toggleSidebar = useUIStore((state) => state.toggleSidebar)
  const setImportExportModalOpen = useUIStore((state) => state.setImportExportModalOpen)
  const addNotification = useUIStore((state) => state.addNotification)
  const allNodes = useNodeStore((state) => state.nodes)
  const cables = useFiberStore((state) => state.cables)
  const rootFolders = useFolderStore((state) => state.getRootFolders())
  const createFolder = useFolderStore((state) => state.createFolder)
  const activeFolderId = useFolderStore((state) => state.activeFolderId)
  const setActiveFolder = useFolderStore((state) => state.setActiveFolder)
  const activeFolder = useFolderStore((state) => state.getActiveFolder())
  const updateNode = useNodeStore((state) => state.updateNode)

  // Filtrar nodos según la carpeta activa (incl. vista "sin carpeta")
  const nodes = filterNodesByFolderView(allNodes, activeFolderId)

  const unfiledCount = useMemo(
    () => allNodes.filter((n: any) => n.folderId == null || n.folderId === '').length,
    [allNodes]
  )

  const visibleCables = useMemo(() => {
    const nodeIdsInView = new Set(nodes.map((n) => n.id))
    return cables.filter((cable: any) => {
      const o = cableOrigenId(cable)
      const d = cableDestinoId(cable)
      return !!(o && d && nodeIdsInView.has(o) && nodeIdsInView.has(d))
    })
  }, [cables, nodes])

  const folderExplorerHint = useMemo(() => {
    if (!activeFolderId) return 'Toda la organización'
    if (isUnfiledFolderView(activeFolderId)) return 'Solo sin carpeta'
    return activeFolder?.name || 'Carpeta'
  }, [activeFolderId, activeFolder])

  const [isCreating, setIsCreating] = useState(false)
  const [newFolderName, setNewFolderName] = useState('')
  const [creating, setCreating] = useState(false)
  const [cablesExpanded, setCablesExpanded] = useState(false)
  const [bulkTypeOpen, setBulkTypeOpen] = useState(false)
  const [bulkSelectedIds, setBulkSelectedIds] = useState<Record<string, boolean>>({})
  const [bulkTargetType, setBulkTargetType] = useState<NodeType>('POSTE')
  const [bulkWorking, setBulkWorking] = useState(false)

  const bulkSelectedCount = useMemo(
    () => Object.values(bulkSelectedIds).filter(Boolean).length,
    [bulkSelectedIds]
  )

  const toggleBulkSelect = (id: string) => {
    setBulkSelectedIds((prev) => ({ ...prev, [id]: !prev[id] }))
  }

  const selectAllVisibleNodes = () => {
    const next: Record<string, boolean> = {}
    for (const n of nodes) next[n.id] = true
    setBulkSelectedIds(next)
  }

  const clearBulkSelection = () => setBulkSelectedIds({})

  const applyBulkTypeChange = async () => {
    const ids = Object.entries(bulkSelectedIds)
      .filter(([, v]) => v)
      .map(([id]) => id)
    if (ids.length === 0) return

    setBulkWorking(true)
    let ok = 0
    let skipped = 0

    try {
      for (const id of ids) {
        const node = allNodes.find((n) => n.id === id) as AnyNode | undefined
        if (!node) {
          skipped++
          continue
        }
        if (!canChangeNodeType(node, cables)) {
          skipped++
          continue
        }
        if (node.tipo === bulkTargetType) {
          skipped++
          continue
        }
        try {
          await updateNode({
            ...node,
            tipo: bulkTargetType,
            config: {},
          } as unknown as AnyNode)
          ok++
        } catch {
          skipped++
        }
      }

      addNotification(
        'success',
        `Tipo actualizado: ${ok} nodo(s)${skipped > 0 ? `; ${skipped} omitidos (cables o config)` : ''}`
      )
      clearBulkSelection()
    } finally {
      setBulkWorking(false)
    }
  }

  // Calcular estadísticas
  const stats = {
    pop: nodes.filter((n) => n.tipo === 'POP').length,
    olt: nodes.filter((n) => (n.tipo as string) === 'OLT').length,
    nap: nodes.filter((n) => n.tipo === 'NAP').length,
    cierre: nodes.filter((n) => n.tipo === 'CIERRE').length,
    cliente: nodes.filter((n) => n.tipo === 'CLIENTE').length,
    poste: nodes.filter((n) => n.tipo === 'POSTE').length,
  }

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return

    setCreating(true)
    try {
      await createFolder(newFolderName.trim())
      setNewFolderName('')
      setIsCreating(false)
    } catch (error) {
      console.error('Error creating folder:', error)
      const msg = error instanceof Error ? error.message : 'Error al crear la carpeta'
      alert(msg)
    } finally {
      setCreating(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleCreateFolder()
    } else if (e.key === 'Escape') {
      setIsCreating(false)
      setNewFolderName('')
    }
  }

  if (!sidebarOpen) {
    return (
      <button
        onClick={toggleSidebar}
        className="absolute left-4 top-20 z-50 p-3 bg-white dark:bg-dark-800 rounded-lg shadow-lg hover:shadow-xl transition-shadow border border-gray-200 dark:border-dark-700"
        title="Abrir panel"
      >
        <Plus className="w-5 h-5" />
      </button>
    )
  }

  return (
    <aside
      className="
        md:static md:translate-x-0 md:w-80
        fixed inset-y-0 left-0 top-0 h-full z-[1200] w-[85vw] max-w-sm
        bg-white dark:bg-dark-800 border-r border-gray-200 dark:border-dark-700
        flex flex-col overflow-hidden shadow-xl md:shadow-none
      "
      style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
    >
      {/* Header */}
      <div className="p-4 border-b border-gray-200 dark:border-dark-700 flex items-center justify-between">
        <h2 className="text-lg font-semibold">Explorador</h2>
        <button
          onClick={toggleSidebar}
          className="p-1 hover:bg-gray-100 dark:hover:bg-dark-700 rounded"
          title="Cerrar panel"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="px-4 py-2 border-b border-gray-200 dark:border-dark-700">
        <button
          type="button"
          onClick={() => setImportExportModalOpen(true)}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm rounded-lg bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 border border-primary-200 dark:border-primary-800 hover:bg-primary-100 dark:hover:bg-primary-900/40"
        >
          <FileUp className="w-4 h-4 shrink-0" />
          Importar / Exportar red
        </button>
        <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-1.5 text-center">
          KML, CSV, imagen del mapa y reportes
        </p>
      </div>

      <div className="px-4 py-2 border-b border-gray-200 dark:border-dark-700">
        <button
          type="button"
          onClick={() => setBulkTypeOpen((o) => !o)}
          className="w-full flex items-center justify-between gap-2 px-3 py-2 text-sm rounded-lg bg-gray-50 dark:bg-dark-700/80 hover:bg-gray-100 dark:hover:bg-dark-700 text-gray-800 dark:text-gray-200"
        >
          <span className="flex items-center gap-2">
            <Users className="w-4 h-4 shrink-0" />
            Cambio masivo de tipo
          </span>
          {bulkTypeOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </button>
        {bulkTypeOpen && (
          <div className="mt-2 space-y-2 text-xs">
            <p className="text-gray-500 dark:text-gray-400">
              Solo nodos sin cables y sin splitters/OLTs/config avanzada. Lista según el explorador:{' '}
              {folderExplorerHint}.
            </p>
            <div className="flex gap-1 flex-wrap">
              <button
                type="button"
                onClick={selectAllVisibleNodes}
                className="px-2 py-1 rounded bg-gray-200 dark:bg-dark-600 hover:bg-gray-300 dark:hover:bg-dark-500"
              >
                Todos
              </button>
              <button
                type="button"
                onClick={clearBulkSelection}
                className="px-2 py-1 rounded bg-gray-200 dark:bg-dark-600 hover:bg-gray-300 dark:hover:bg-dark-500"
              >
                Ninguno
              </button>
            </div>
            <div className="max-h-36 overflow-y-auto border border-gray-200 dark:border-dark-600 rounded-md p-1 space-y-0.5">
              {nodes.length === 0 ? (
                <p className="text-gray-500 p-2">No hay nodos en esta vista</p>
              ) : (
                nodes.map((n: AnyNode) => (
                  <label
                    key={n.id}
                    className="flex items-center gap-2 px-1 py-0.5 rounded hover:bg-gray-100 dark:hover:bg-dark-700 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={!!bulkSelectedIds[n.id]}
                      onChange={() => toggleBulkSelect(n.id)}
                      className="w-3.5 h-3.5 rounded border-gray-300"
                    />
                    <span className="truncate flex-1" title={n.nombre || n.id}>
                      {NODE_ICONS[n.tipo]} {n.nombre || n.refCode || n.id.slice(0, 8)}
                    </span>
                    <span className="text-[10px] text-gray-500 shrink-0">{n.tipo}</span>
                  </label>
                ))
              )}
            </div>
            <div className="flex items-center gap-2">
              <select
                value={bulkTargetType}
                onChange={(e) => setBulkTargetType(e.target.value as NodeType)}
                className="flex-1 min-w-0 px-2 py-1.5 rounded border border-gray-300 dark:border-dark-600 dark:bg-dark-700 text-xs"
              >
                {BULK_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
              <button
                type="button"
                disabled={bulkWorking || bulkSelectedCount === 0}
                onClick={() => void applyBulkTypeChange()}
                className="shrink-0 px-3 py-1.5 rounded-lg bg-primary-600 text-white text-xs disabled:opacity-50 hover:bg-primary-700"
              >
                {bulkWorking ? '…' : 'Aplicar'}
              </button>
            </div>
            {bulkSelectedCount > 0 && (
              <p className="text-gray-500">{bulkSelectedCount} seleccionado(s)</p>
            )}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {/* Sección de Carpetas */}
        <div className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase">
              Carpetas / Proyectos
            </h3>
            <button
              onClick={() => setIsCreating(true)}
              className="p-1 hover:bg-gray-100 dark:hover:bg-dark-700 rounded"
              title="Nueva carpeta"
            >
              <FolderPlus className="w-4 h-4" />
            </button>
          </div>

          <p className="text-[10px] text-gray-500 dark:text-gray-400 mb-3 leading-snug">
            El mapa y el explorador muestran solo la{' '}
            <strong className="font-medium text-gray-700 dark:text-gray-300">organización de tu sesión</strong>{' '}
            (no mezcla con otras). Las carpetas filtran nodos que hayas{' '}
            <strong className="font-medium text-gray-700 dark:text-gray-300">asignado</strong> a cada carpeta; el resto
            queda en &quot;Sin carpeta&quot; o en &quot;Toda la organización&quot;.
          </p>

          {/* Toda la organización */}
          <button
            type="button"
            onClick={() => setActiveFolder(null)}
            title="Ver todos los nodos de tu organización (no es otra empresa)"
            className={`w-full mb-2 px-3 py-2 rounded-lg flex items-center gap-2 transition-colors ${
              activeFolderId === null
                ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 border-2 border-primary-500'
                : 'bg-gray-50 dark:bg-dark-700 hover:bg-gray-100 dark:hover:bg-dark-600 border-2 border-transparent'
            }`}
          >
            <Layers className="w-4 h-4 shrink-0" />
            <span className="font-medium text-sm text-left">Toda la organización</span>
            <span className="ml-auto text-xs text-gray-500 shrink-0">{allNodes.length}</span>
          </button>

          {/* Solo nodos sin carpeta */}
          <button
            type="button"
            onClick={() => setActiveFolder(UNFILED_FOLDER_SENTINEL)}
            title="Nodos que aún no están en ninguna carpeta"
            className={`w-full mb-2 px-3 py-2 rounded-lg flex items-center gap-2 transition-colors ${
              activeFolderId === UNFILED_FOLDER_SENTINEL
                ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-900 dark:text-amber-200 border-2 border-amber-500'
                : 'bg-gray-50 dark:bg-dark-700 hover:bg-gray-100 dark:hover:bg-dark-600 border-2 border-transparent'
            }`}
          >
            <FolderPlus className="w-4 h-4 shrink-0 opacity-80" />
            <span className="font-medium text-sm text-left">Sin carpeta</span>
            <span className="ml-auto text-xs text-gray-500 shrink-0">{unfiledCount}</span>
          </button>

          {/* Formulario para crear carpeta */}
          {isCreating && (
            <div className="mb-3 flex gap-2">
              <input
                type="text"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Nombre de carpeta..."
                className="flex-1 px-2 py-1 text-sm border rounded focus:ring-2 focus:ring-primary-500 dark:bg-dark-700 dark:border-dark-600"
                autoFocus
                disabled={creating}
              />
              <button
                onClick={handleCreateFolder}
                disabled={creating || !newFolderName.trim()}
                className="p-1 bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50"
                title="Crear"
              >
                <Check className="w-4 h-4" />
              </button>
              <button
                onClick={() => {
                  setIsCreating(false)
                  setNewFolderName('')
                }}
                className="p-1 hover:bg-gray-100 dark:hover:bg-dark-700 rounded"
                title="Cancelar"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {rootFolders.length > 0 ? (
            <FolderTree folders={rootFolders} />
          ) : (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              <p className="text-sm mb-2">Sin carpetas</p>
              <button 
                onClick={() => setIsCreating(true)}
                className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
              >
                Crear carpeta raíz
              </button>
            </div>
          )}
        </div>

        {/* Sección de Cables */}
        <div className="p-4 border-t border-gray-200 dark:border-dark-700">
          <button
            onClick={() => setCablesExpanded(!cablesExpanded)}
            className="w-full flex items-center justify-between mb-3"
          >
            <h3 className="text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase flex items-center gap-2">
              <Cable className="w-4 h-4" />
              Cables ({visibleCables.length})
            </h3>
            {cablesExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </button>
          
          {cablesExpanded && (
            <CablesList cables={visibleCables} nodes={nodes} />
          )}
        </div>

        {/* Sección de Estadísticas */}
        <div className="p-4 border-t border-gray-200 dark:border-dark-700">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase">
              Estadísticas
            </h3>
            {activeFolderId !== null && (
              <span className="text-xs px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded">
                {isUnfiledFolderView(activeFolderId) ? 'Sin carpeta' : activeFolder?.name || 'Filtrado'}
              </span>
            )}
          </div>

          <div className="space-y-2">
            <StatItem
              icon={NODE_ICONS.POP}
              label="POPs"
              value={stats.pop}
              color="text-green-600 dark:text-green-400"
            />
            <StatItem
              icon={NODE_ICONS.OLT || '📡'}
              label="OLTs"
              value={stats.olt}
              color="text-cyan-600 dark:text-cyan-400"
            />
            <StatItem
              icon={NODE_ICONS.NAP}
              label="NAPs"
              value={stats.nap}
              color="text-blue-600 dark:text-blue-400"
            />
            <StatItem
              icon={NODE_ICONS.CIERRE}
              label="Cierres"
              value={stats.cierre}
              color="text-orange-600 dark:text-orange-400"
            />
            <StatItem
              icon={NODE_ICONS.CLIENTE}
              label="Clientes"
              value={stats.cliente}
              color="text-purple-600 dark:text-purple-400"
            />
            <StatItem
              icon={NODE_ICONS.POSTE}
              label="Postes"
              value={stats.poste}
              color="text-gray-600 dark:text-gray-400"
            />
          </div>

          {/* Total */}
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-dark-700">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">
                {activeFolderId === null
                  ? 'Total nodos'
                  : isUnfiledFolderView(activeFolderId)
                    ? 'Sin carpeta'
                    : 'Nodos en carpeta'}
              </span>
              <span className="text-lg font-bold text-primary-600 dark:text-primary-400">
                {nodes.length}
              </span>
            </div>
            {activeFolderId !== null && (
              <div className="flex items-center justify-between text-xs text-gray-500 mt-1">
                <span>En toda la organización</span>
                <span>{allNodes.length}</span>
              </div>
            )}
          </div>
        </div>

        {/* Sección de Filtros */}
        <div className="p-4 border-t border-gray-200 dark:border-dark-700">
          <h3 className="text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase mb-3">
            Visibilidad
          </h3>
          <NodeTypeFilters />
        </div>
      </div>
    </aside>
  )
}

// Componente auxiliar para items de estadística
function StatItem({
  icon,
  label,
  value,
  color,
}: {
  icon: string
  label: string
  value: number
  color: string
}) {
  return (
    <div className="flex items-center justify-between p-2 rounded hover:bg-gray-50 dark:hover:bg-dark-700">
      <div className="flex items-center gap-2">
        <span className="text-lg">{icon}</span>
        <span className="text-sm">{label}</span>
      </div>
      <span className={`font-bold ${color}`}>{value}</span>
    </div>
  )
}

// Componente de filtros de tipos de nodo
function NodeTypeFilters() {
  const visibleNodeTypes = useUIStore((state) => state.visibleNodeTypes)
  const toggleNodeTypeVisibility = useUIStore((state) => state.toggleNodeTypeVisibility)

  const nodeTypes = [
    { type: 'POP', label: 'POPs', icon: NODE_ICONS.POP, color: NODE_COLORS.POP },
    { type: 'OLT', label: 'OLTs', icon: NODE_ICONS.OLT || '📡', color: '#06B6D4' },
    { type: 'NAP', label: 'NAPs', icon: NODE_ICONS.NAP, color: NODE_COLORS.NAP },
    { type: 'CIERRE', label: 'Cierres', icon: NODE_ICONS.CIERRE, color: NODE_COLORS.CIERRE },
    { type: 'CLIENTE', label: 'Clientes', icon: NODE_ICONS.CLIENTE, color: NODE_COLORS.CLIENTE },
    { type: 'POSTE', label: 'Postes', icon: NODE_ICONS.POSTE, color: NODE_COLORS.POSTE },
  ]

  return (
    <div className="space-y-2">
      {nodeTypes.map(({ type, label, icon }) => {
        const isVisible = visibleNodeTypes.has(type)

        return (
          <label
            key={type}
            className="flex items-center gap-2 p-2 rounded hover:bg-gray-50 dark:hover:bg-dark-700 cursor-pointer"
          >
            <input
              type="checkbox"
              checked={isVisible}
              onChange={() => toggleNodeTypeVisibility(type)}
              className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span className="text-lg">{icon}</span>
            <span className="text-sm flex-1">{label}</span>
          </label>
        )
      })}
    </div>
  )
}

// Componente para listar cables
function CablesList({ cables, nodes }: { cables: any[]; nodes: any[] }) {
  const deleteCable = useFiberStore((state) => state.deleteCable)
  const setSelectedCable = useFiberStore((state) => state.setSelectedCable)
  const selectedCableId = useFiberStore((state) => state.selectedCableId)
  const addNotification = useUIStore((state) => state.addNotification)

  const getNodeName = (nodeId: string) => {
    const node = nodes.find(n => n.id === nodeId)
    return node?.nombre || node?.id?.slice(-6) || 'Desconocido'
  }

  const getTipoColor = (tipo: string) => {
    switch (tipo) {
      case 'TRONCAL': return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
      case 'DISTRIBUCION': return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
      case 'ACOMETIDA': return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
      case 'DROP': return 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
      default: return 'bg-gray-100 text-gray-700'
    }
  }

  const handleDelete = async (cableId: string, e: React.MouseEvent) => {
    e.stopPropagation()
    if (confirm('¿Estás seguro de eliminar este cable?')) {
      try {
        await deleteCable(cableId)
        addNotification('success', 'Cable eliminado correctamente')
      } catch {
        addNotification('error', 'Error al eliminar el cable')
      }
    }
  }

  if (cables.length === 0) {
    return (
      <div className="text-center py-4 text-gray-500 text-sm">
        <Cable className="w-6 h-6 mx-auto mb-2 opacity-50" />
        <p>Sin cables</p>
        <p className="text-xs mt-1">Usa el botón "Agregar Cable" en el toolbar</p>
      </div>
    )
  }

  return (
    <div className="space-y-2 max-h-60 overflow-y-auto">
      {cables.map((cable) => (
        <div
          key={cable.id}
          onClick={() => setSelectedCable(cable.id)}
          className={`p-2 border rounded-lg cursor-pointer transition-colors ${
            selectedCableId === cable.id
              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
              : 'border-gray-200 dark:border-dark-600 hover:border-blue-300'
          }`}
        >
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <div className="text-xs font-semibold truncate mb-0.5">
                {cableLabelShort(cable as any)}
              </div>
              <div className="flex items-center gap-2 mb-1">
                <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${getTipoColor(cable.tipo)}`}>
                  {cable.tipo}
                </span>
                <span className="text-xs font-medium truncate">
                  {cable.numeroFibras} FO
                </span>
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                <span>{getNodeName(cable.origenId || cable.origen)}</span>
                <span>→</span>
                <span>{getNodeName(cable.destinoId || cable.destino)}</span>
              </div>
              {cable.longitudTotal && (
                <div className="text-[10px] text-gray-400 mt-1">
                  {(cable.longitudTotal / 1000).toFixed(2)} km
                </div>
              )}
            </div>
            <button
              onClick={(e) => handleDelete(cable.id, e)}
              className="p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
              title="Eliminar cable"
            >
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        </div>
      ))}
    </div>
  )
}
